import { Download } from "lucide-react";
import { PageHeaderTag } from "components/shared/Tags/StatusTags";
import { getPoStatusDisplayConfig } from "../poUtils";
import { PO_STATUS } from "../../../../utils/constants/others";
import { Loader } from "../../Common/Loader";
import ShowToast from "../../Common/Toast";
import GenerateUpcModal from "../../EmbeddedUpc/GenerateUpcModal";
import {
  formatWeight,
  PurchaseOrderButton,
  PurchaseOrderConfirmationModal,
  PurchaseOrderContactModal,
  PurchaseOrderFooter,
  PurchaseOrderHeader,
  PurchaseOrderProducts,
  PurchaseOrderSuccessModal,
  PurchaseOrderSummary,
} from "./PoDetailsElements";
import PoDisputeForm from "./PoDisputeForm";
import styles from "./PoDetails.module.scss";
import { strings } from "utils/constants/strings";

export default function PoDetailsMobile({
  purchaseOrder,
  items,
  totalWeight,
  isLoading,
  hasError,
  isPending,
  canAccept,
  canDispute,
  canGenerateUpc,
  documentUrl,
  phoneNumber,
  creatorName,
  goToList,
  goToPoDetails,
  downloadPurchaseOrder,
  callPurchaseOrderCreator,
  showContactDetails,
  setShowContactDetails,
  showConfirmation,
  setShowConfirmation,
  accepting,
  accepted,
  acceptPurchaseOrder,
  showDisputeForm,
  openDispute,
  closeDispute,
  dispute,
  handleGenerateQr,
  checkingComplianceFor,
  upcModalItem,
  closeUpcModal,
  complianceError,
  clearComplianceError,
}) {
  if (isLoading) return <Loader />;
  if (hasError) {
    return <p className="fs-14 text-center" style={{ padding: 32 }}>Could not load this purchase order.</p>;
  }

  const summary = `${items.length} SKUs • ${formatWeight(totalWeight)}`;

  if (showDisputeForm) {
    return (
      <main className={styles.page}>
        <PurchaseOrderHeader number={purchaseOrder.purchase_order_number} summary={summary} onBack={closeDispute} />
        <div className={styles.content}>
          <PoDisputeForm variant="page" items={items} dispute={dispute}/>
        </div>
      </main>
    );
  }

  const headerActions = [
    documentUrl && {
      key: "download",
      label: "Download purchase order",
      icon: <Download size={19} />,
      onClick: downloadPurchaseOrder,
    },
    phoneNumber && {
      key: "call",
      label: "Call purchase order creator",
      icon: <img src="/logos/call.png" width="40" height="40" alt="" />,
      onClick: () => setShowContactDetails(true),
    },
  ].filter(Boolean);

  return (
    <main className={styles.page}>
      <PurchaseOrderHeader
        number={purchaseOrder.purchase_order_number}
        summary={summary}
        backPath="/po/list"
        actions={headerActions}
      />
      {(() => {
        const statusValue =
          purchaseOrder.po_status === PO_STATUS.WAITING_FOR_VENDOR_APPROVAL ||
          purchaseOrder.po_status === PO_STATUS.COMPLETED
            ? purchaseOrder.po_status
            : purchaseOrder.derived_status || purchaseOrder.po_status;
        const { label, bgColor, textColor } = getPoStatusDisplayConfig(statusValue);
        return (
          <PageHeaderTag
            text={label}
            textColor={textColor}
            bgColor={bgColor}
          />
        );
      })()}

      <div className={styles.content}>
        <PurchaseOrderSummary purchaseOrder={purchaseOrder} />
        <PurchaseOrderProducts
          items={items}
          canGenerateUpc={canGenerateUpc}
          checkingComplianceFor={checkingComplianceFor}
          onGenerateQr={handleGenerateQr}
          disputeDetails={purchaseOrder?.dispute_details}
        />
      </div>

      {(canDispute || canAccept) && (
        <PurchaseOrderFooter single={!canDispute}>
          {canDispute && (
            <PurchaseOrderButton type="button" secondary onClick={openDispute}>
              Raise dispute
            </PurchaseOrderButton>
          )}
          {canAccept && (
            <PurchaseOrderButton type="button" onClick={() => setShowConfirmation(true)}>
              Accept PO
            </PurchaseOrderButton>
          )}
        </PurchaseOrderFooter>
      )}

      <PurchaseOrderConfirmationModal
        visible={showConfirmation && !accepted}
        purchaseOrder={purchaseOrder}
        items={items}
        totalWeight={totalWeight}
        submitting={accepting}
        onBack={() => setShowConfirmation(false)}
        onConfirm={acceptPurchaseOrder}
      />
      <PurchaseOrderSuccessModal visible={accepted} title="PO Accepted" onClose={goToList} />
      <PurchaseOrderContactModal
        contact={showContactDetails ? { name: creatorName, phoneNumber } : null}
        onClose={() => setShowContactDetails(false)}
        onCall={callPurchaseOrderCreator}
      />
      <PurchaseOrderSuccessModal visible={dispute.submitted} title={strings.grnModalDisputeRaised} onClose={dispute.onSuccessModalClose} />
      <GenerateUpcModal visible={!!upcModalItem} item={upcModalItem} poId={purchaseOrder.id} onClose={closeUpcModal} />
      {complianceError && <ShowToast errorMsg={complianceError} type="error" onClose={clearComplianceError} />}
    </main>
  );
}
